// src/components/ArtifactToggleButton.tsx
import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText } from 'lucide-react';
import { useArtifacts } from '@/contexts/ArtifactContext';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface ArtifactToggleButtonProps {
  className?: string;
  tools?: string[]; // Array of enabled tools for the current assistant
}

export const ArtifactToggleButton: React.FC<ArtifactToggleButtonProps> = ({ 
  className = "",
  tools = []
}) => {
  const { artifacts, isPanelOpen, setPanelOpen, activeArtifactId, setActiveArtifactId } = useArtifacts();

  // Don't render if the artifacts tool is not enabled for this assistant
  const hasArtifactsTool = tools.includes('artifacts');
  if (!hasArtifactsTool) {
    return null;
  }

  // If the artifacts tool is enabled but no artifacts exist yet,
  // show a placeholder state to help users discover the feature
  const hasArtifacts = artifacts.size > 0;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="lg"
          className={`px-4 gap-2 ${className} ${!hasArtifacts ? 'opacity-60' : ''}`}
          onClick={() => {
            if (!hasArtifacts) {
              // If no artifacts yet, don't do anything
              return;
            }
            
            if (!isPanelOpen) {
              // If opening panel, ensure there's an active artifact
              if (!activeArtifactId && artifacts.size > 0) {
                const firstArtifactId = Array.from(artifacts.keys())[0];
                setActiveArtifactId(firstArtifactId);
              }
            }
            
            setPanelOpen(!isPanelOpen);
          }}
          disabled={!hasArtifacts}
        >
          <FileText className="h-5 w-5" />
          <span className="hidden sm:inline">Artifacts</span>
          {hasArtifacts ? (
            <Badge variant="secondary" className="text-xs">
              {artifacts.size}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs opacity-50">
              0
            </Badge>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {!hasArtifacts 
          ? 'Artifacts will appear here when created'
          : `${isPanelOpen ? 'Hide' : 'Show'} artifact panel (${artifacts.size} item${artifacts.size !== 1 ? 's' : ''})`
        }
      </TooltipContent>
    </Tooltip>
  );
};

export default ArtifactToggleButton;